import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Close, Delete } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import BWITextField from '../shared/BWITextField';
import { ReleaseNote, ReleaseNoteInput } from '../../state/types';

interface ReleaseNoteModalProps {
  open: boolean;
  onClose: () => void;
  note?: ReleaseNote | null;
  onSave: (input: ReleaseNoteInput, noteId?: string) => Promise<void>;
  onDelete?: (noteId: string) => Promise<void>;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const ReleaseNoteModal: React.FC<ReleaseNoteModalProps> = ({
  open,
  onClose,
  note,
  onSave,
  onDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';
  const accentRed = theme.palette.status.accent;

  const [version, setVersion] = useState('');
  const [releaseDate, setReleaseDate] = useState(todayIso());
  const [bullets, setBullets] = useState<string[]>(['']);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (note) {
      setVersion(note.version);
      setReleaseDate(note.releaseDate);
      setBullets(note.bullets.length ? note.bullets : ['']);
    } else {
      setVersion('');
      setReleaseDate(todayIso());
      setBullets(['']);
    }
  }, [open, note]);

  const updateBullet = (index: number, value: string) => {
    setBullets((prev) => prev.map((b, i) => (i === index ? value : b)));
  };

  const addBullet = () => setBullets((prev) => [...prev, '']);

  const removeBullet = (index: number) => {
    setBullets((prev) => (prev.length === 1 ? [''] : prev.filter((_, i) => i !== index)));
  };

  const handleSave = async () => {
    const cleanedBullets = bullets.map((b) => b.trim()).filter(Boolean);
    if (!version.trim()) {
      alert('Please enter a version.');
      return;
    }
    if (!releaseDate.trim()) {
      alert('Please pick a release date.');
      return;
    }
    if (!cleanedBullets.length) {
      alert('Add at least one bullet describing the changes.');
      return;
    }
    setSubmitting(true);
    try {
      await onSave(
        { version: version.trim(), releaseDate, bullets: cleanedBullets },
        note?.noteId,
      );
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!note || !onDelete) return;
    if (!window.confirm(`Delete release note for ${note.version}?`)) return;
    setSubmitting(true);
    try {
      await onDelete(note.noteId);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const primaryButtonSx = {
    backgroundColor: accentRed,
    color: '#fff',
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '10px',
    py: 1,
    boxShadow: `0 1px 0 ${alpha('#fff', 0.06)} inset, 0 6px 16px -8px ${alpha(accentRed, 0.6)}`,
    '&:hover': {
      backgroundColor: accentRed,
      boxShadow: `0 0 0 4px ${alpha(accentRed, 0.18)}, 0 8px 24px -8px ${alpha(accentRed, 0.7)}`,
    },
    '&:disabled': {
      backgroundColor: alpha(accentRed, 0.5),
      color: alpha('#fff', 0.7),
    },
  } as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: isMobile ? 0 : '18px',
          border: isMobile ? 'none' : `1px solid ${alpha(isDark ? '#fff' : '#000', 0.08)}`,
          backgroundColor: isDark ? '#141416' : '#fff',
          backgroundImage: isDark
            ? `linear-gradient(180deg, ${alpha('#fff', 0.025)} 0%, ${alpha('#fff', 0)} 30%)`
            : 'none',
          boxShadow: isDark
            ? '0 24px 60px -12px rgba(0,0,0,0.7)'
            : '0 24px 60px -20px rgba(15,23,42,0.25)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          fontSize: '1.05rem',
          letterSpacing: '-0.005em',
          pt: 2.5,
          pb: 1.5,
        }}
      >
        {note ? 'Edit release note' : 'Add release note'}
        <IconButton
          onClick={onClose}
          data-cy="release-note-modal-close-btn"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: alpha(isDark ? '#fff' : '#000', 0.06),
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <BWITextField
            label="Version"
            placeholder="e.g. 10.2.0"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            data-cy="release-note-version-input"
          />
          <BWITextField
            label="Release date"
            type="date"
            value={releaseDate}
            onChange={(e) => setReleaseDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            data-cy="release-note-date-input"
          />
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 600,
                display: 'block',
                mb: 1,
              }}
            >
              Changes
            </Typography>
            <Stack spacing={1.25}>
              {bullets.map((bullet, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <BWITextField
                    placeholder={`Bullet ${index + 1}`}
                    value={bullet}
                    onChange={(e) => updateBullet(index, e.target.value)}
                    multiline
                    data-cy={`release-note-bullet-input-${index}`}
                  />
                  <IconButton
                    onClick={() => removeBullet(index)}
                    aria-label="Remove bullet"
                    sx={{
                      mt: 0.5,
                      color: 'text.secondary',
                      '&:hover': {
                        color: theme.palette.status.error,
                        backgroundColor: alpha(theme.palette.status.error, 0.08),
                      },
                    }}
                    data-cy={`release-note-bullet-remove-${index}`}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                startIcon={<AddIcon />}
                onClick={addBullet}
                variant="text"
                size="small"
                sx={{
                  alignSelf: 'flex-start',
                  textTransform: 'none',
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: alpha(isDark ? '#fff' : '#000', 0.04),
                  },
                }}
                data-cy="release-note-add-bullet-btn"
              >
                Add bullet
              </Button>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1.5, flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleSave}
          disabled={submitting}
          sx={primaryButtonSx}
          data-cy="release-note-save-btn"
        >
          {note ? 'Save changes' : 'Add release'}
        </Button>
        {note && onDelete && (
          <Button
            fullWidth
            variant="text"
            onClick={handleDelete}
            disabled={submitting}
            sx={{
              textTransform: 'none',
              color: theme.palette.status.error,
              borderRadius: '10px',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: alpha(theme.palette.status.error, 0.08),
              },
            }}
            data-cy="release-note-delete-btn"
          >
            Delete release note
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseNoteModal;
